<script setup lang="ts">
/**
 * The base raised surface. Everything that groups content on a page sits on one
 * of these: KPI cards, list containers, document sections, right-rail blocks.
 */

withDefaults(
  defineProps<{
    /** `flat` drops the elevation for cards inside an already-raised surface. */
    variant?: 'raised' | 'flat' | 'sunken'
    padding?: 'none' | 'sm' | 'md' | 'lg'
    /** Adds hover affordance; pair with a click handler or a wrapping link. */
    interactive?: boolean
    /** Semantic element; use `section`/`article` where the content warrants it. */
    as?: 'div' | 'section' | 'article' | 'li'
  }>(),
  { variant: 'raised', padding: 'md', as: 'div' },
)
</script>

<template>
  <component
    :is="as"
    class="e-card"
    :class="[
      `e-card--${variant}`,
      `e-card--pad-${padding}`,
      { 'e-card--interactive': interactive },
    ]"
  >
    <slot />
  </component>
</template>

<style>
.e-card {
  display: block;
  min-width: 0;
  border-radius: var(--erpx-radius);
  border: 1px solid var(--erpx-color-border-subtle);
}

.e-card--raised {
  background-color: var(--erpx-color-surface-raised);
  box-shadow: var(--erpx-shadow-1);
}

.e-card--flat {
  background-color: var(--erpx-color-surface-raised);
}

.e-card--sunken {
  background-color: var(--erpx-color-surface-sunken);
}

.e-card--pad-none { padding: 0; }
.e-card--pad-sm { padding: var(--erpx-space-3); }
.e-card--pad-md { padding: var(--erpx-space-4); }
.e-card--pad-lg { padding: var(--erpx-space-6); }

.e-card--interactive {
  cursor: pointer;
  transition:
    border-color var(--erpx-duration-fast) var(--erpx-easing-standard),
    box-shadow var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-card--interactive:hover {
  border-color: var(--erpx-color-border-strong);
  box-shadow: var(--erpx-shadow-2);
}

.e-card--interactive:focus-within {
  box-shadow: var(--erpx-focus-ring);
}
</style>
